import { h } from 'preact';
import { useState } from 'preact/hooks';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import { useAuth } from '../hooks/useAuth.js';
import { api } from '../state/auth.js';
import { plan, MODULE_INFO } from '../utils/plan.js';

const TIER_RANK = { basis: 0, plus: 1, professional: 2 };

// A module outside the club's plan: what it offers, which plan opens it, and for a club
// admin the switch to that plan (active right away; 4U-beheer takes it over for the invoice)
export default function PlanLockedView({ lock }) {
  const { hasPermission } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const info = MODULE_INFO[lock.module] || { icon: '🔒', title: 'Deze module', pitch: '' };
  const canUpgrade = hasPermission('settings', 'update');
  const current = plan.value?.tierLabel;
  // Everything else the plan opens on top of the current one
  const alsoOpens = (plan.value?.locked || [])
    .filter(l => l.module !== lock.module && TIER_RANK[l.tier] <= TIER_RANK[lock.tier])
    .map(l => MODULE_INFO[l.module]).filter(Boolean);

  async function upgrade() {
    setBusy(true); setError(null);
    try {
      await api('/api/clubs/subscription', { method: 'PATCH', body: JSON.stringify({ tier: lock.tier }) });
      // Menus and pages load their data per club: start fresh with the new plan
      window.location.reload();
    } catch (err) {
      setError(err.code === 'not_an_upgrade' ? 'Je club heeft dit pakket al. Vernieuw de pagina.' : 'Overstappen lukte niet. Probeer het opnieuw.');
      setBusy(false);
    }
  }

  return (
    <div style="max-width:640px">
      <div class="page-header" style="margin-bottom:8px">
        <h1 class="page-title">{info.icon} {info.title}</h1>
      </div>
      <Card>
        <div style="display:flex;flex-direction:column;gap:14px">
          <div>
            <span style="display:inline-block;font-size:12px;font-weight:600;padding:2px 10px;border-radius:10px;background:var(--color-bg-secondary);border:1px solid var(--color-border)">
              🔒 Onderdeel van {lock.tierLabel}
            </span>
          </div>
          <p style="margin:0;line-height:1.6">{info.pitch}</p>
          {alsoOpens.length > 0 && (
            <div>
              <div class="text-sm text-muted" style="margin-bottom:6px">Met {lock.tierLabel} krijg je er ook bij:</div>
              <ul style="margin:0;padding-left:18px;line-height:1.7;font-size:14px">
                {alsoOpens.map(m => <li key={m.title}>{m.icon} {m.title}</li>)}
              </ul>
            </div>
          )}

          {canUpgrade ? (
            confirming ? (
              <div style="border:1px solid var(--color-border);border-radius:8px;padding:14px;background:var(--color-bg-secondary)">
                <p style="margin:0 0 12px;line-height:1.6;font-size:14px">
                  Je club stapt over van {current || 'het huidige pakket'} naar <strong>{lock.tierLabel}</strong> (€{lock.priceMonthly} per maand).
                  Alles van {lock.tierLabel} is direct beschikbaar.
                </p>
                {error && <div class="alert alert-error mb-4">{error}</div>}
                <div class="flex gap-2" style="flex-wrap:wrap">
                  <Button variant="primary" onClick={upgrade} disabled={busy}>{busy ? 'Bezig…' : `Ja, overstappen naar ${lock.tierLabel}`}</Button>
                  <Button variant="secondary" onClick={() => setConfirming(false)} disabled={busy}>Annuleren</Button>
                </div>
              </div>
            ) : (
              <div class="flex gap-2" style="align-items:center;flex-wrap:wrap">
                <Button variant="primary" onClick={() => setConfirming(true)}>Overstappen naar {lock.tierLabel}</Button>
                <span class="text-sm text-muted">€{lock.priceMonthly} per maand · direct actief</span>
              </div>
            )
          ) : (
            <p class="text-sm text-muted" style="margin:0">
              Je club heeft nu {current ? `het pakket ${current}` : 'een ander pakket'}. Wil je dit gebruiken? Vraag je clubbeheerder om over te stappen naar {lock.tierLabel}.
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}
